import { useState, useEffect, FormEvent } from "react";
import {
  Box, Typography, Paper, TextField, Button, IconButton, Chip,
  List, ListItem, ListItemText, ListSubheader, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { incomeService } from "@/api/income.service";
import { useAuth } from "@/context/AuthContext";
import type { HouseholdIncome, IncomeSource } from "@/api/types";

const MONTHS_FULL = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const fmt = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const toMonthInput = (month: number, year: number) =>
  `${year}-${String(month).padStart(2, "0")}`;

const nowISO = () => {
  const d = new Date();
  return toMonthInput(d.getMonth() + 1, d.getFullYear());
};

const now = new Date();
const MONTH_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
  return { value: `${d.getFullYear()}-${d.getMonth() + 1}`, month: d.getMonth() + 1, year: d.getFullYear(), label: `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}` };
});

export default function RevenuPage() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [monthInput, setMonthInput] = useState(nowISO());
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Household-wide income sources + filters
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [selectedMonthKey, setSelectedMonthKey] = useState("all");
  const [data, setData] = useState<HouseholdIncome | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const selectedMonth = MONTH_OPTIONS.find(o => o.value === selectedMonthKey);

  const loadSources = async () => {
    setListLoading(true); setListError("");
    try {
      const params = selectedMonth
        ? { userId: selectedUserId, month: selectedMonth.month, year: selectedMonth.year }
        : { userId: selectedUserId, month: "all" as const };
      setData(await incomeService.getAll(params));
    } catch (e: any) {
      // Not (yet) part of a household: fall back to a solo view of my own sources.
      if (user && String(e.response?.data?.error || "").includes("foyer")) {
        setData({
          members: [{ id: user.id, name: user.name }],
          sources: selectedMonth
            ? user.incomeSources.filter(s => s.month === selectedMonth.month && s.year === selectedMonth.year)
            : user.incomeSources,
        });
      } else {
        setListError(e.response?.data?.error || "Erreur.");
      }
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadSources(); }, [selectedUserId, selectedMonthKey, user]);

  const sources = data?.sources || [];
  const members = data?.members || [];

  const ownerLabel = (s: IncomeSource) => s.userId === user?.id ? "Moi" : (s.userName || "");
  const canEdit = (s: IncomeSource) => !s.userId || s.userId === user?.id;

  const groups = (() => {
    const result: { key: string; label: string; total: number; sources: IncomeSource[] }[] = [];
    for (const src of sources) {
      const key = `${src.year}-${src.month}`;
      let g = result.find(g => g.key === key);
      if (!g) { g = { key, label: `${MONTHS_FULL[src.month - 1]} ${src.year}`, total: 0, sources: [] }; result.push(g); }
      g.sources.push(src);
      g.total += src.amount;
    }
    return result;
  })();

  const openAdd = () => {
    setEditingId(null);
    setLabel(""); setAmount(""); setMonthInput(nowISO()); setError("");
    setOpen(true);
  };

  const openEdit = (src: IncomeSource) => {
    setEditingId(src.id);
    setLabel(src.label);
    setAmount(String(src.amount));
    setMonthInput(toMonthInput(src.month, src.year));
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const [y, m] = monthInput.split("-").map(Number);
      if (editingId) {
        await incomeService.update(editingId, { label, amount: parseFloat(amount), month: m, year: y });
      } else {
        await incomeService.add({ label, amount: parseFloat(amount), month: m, year: y });
      }
      await Promise.all([refreshUser(), loadSources()]);
      setOpen(false);
    } catch (e: any) { setError(e.response?.data?.error || "Erreur."); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id: string) => {
    await incomeService.remove(id);
    await Promise.all([refreshUser(), loadSources()]);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: "auto" }}>
      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <Box sx={{ px: 3, pt: 3, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" fontWeight={600}>Revenus du foyer</Typography>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAdd}>Ajouter</Button>
        </Box>

        <Box sx={{ px: 3, pb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Membre</InputLabel>
            <Select value={selectedUserId} label="Membre" onChange={e => setSelectedUserId(e.target.value)}>
              <MenuItem value="all">Tous</MenuItem>
              {members.map(m => <MenuItem key={m.id} value={m.id}>{m.id === user?.id ? "Moi" : m.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Mois</InputLabel>
            <Select value={selectedMonthKey} label="Mois" onChange={e => setSelectedMonthKey(e.target.value)}>
              <MenuItem value="all">Tous</MenuItem>
              {MONTH_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {listError && <Alert severity="error" sx={{ mx: 3, mb: 2 }}>{listError}</Alert>}
        <Divider />
        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <List>
            {groups.length === 0 && <ListItem><ListItemText secondary="Aucune source de revenu" /></ListItem>}
            {groups.map(group => [
              <ListSubheader key={group.key} sx={{ bgcolor: "action.hover", borderRadius: 1, minHeight: 40, lineHeight: "normal", display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, my: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>{group.label}</Typography>
                <Typography variant="caption" color="text.secondary">{fmt(group.total)}</Typography>
              </ListSubheader>,
              ...group.sources.map(src => (
                <ListItem key={src.id} secondaryAction={canEdit(src) && (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton edge="end" size="small" onClick={() => openEdit(src)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton edge="end" size="small" color="error" onClick={() => handleDelete(src.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                )}>
                  <ListItemText
                    primary={<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      {src.label}
                      {selectedUserId === "all" && <Chip label={ownerLabel(src)} size="small" variant="outlined" />}
                    </Box>}
                    secondary={fmt(src.amount)}
                  />
                </ListItem>
              )),
            ])}
          </List>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingId ? "Modifier la source de revenu" : "Ajouter une source de revenu"}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Libellé" value={label} onChange={e => setLabel(e.target.value)} required fullWidth placeholder="Ex : Salaire, Freelance…" />
            <TextField label="Montant (€)" type="number" value={amount} onChange={e => setAmount(e.target.value)} required fullWidth inputProps={{ min: 0, step: 0.01 }} />
            <TextField label="Mois" type="month" value={monthInput} onChange={e => setMonthInput(e.target.value)} required fullWidth InputLabelProps={{ shrink: true }} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <CircularProgress size={20} /> : editingId ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
